import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Skeleton } from '@etnas/design-system';
import type { RoleKey } from '@/api';
import { Guard, RequireWorkforce, RootRedirect, WorkspaceLayout } from './shell/Shell';
import { ChartLayout } from './chart/ChartLayout';
import { PatientSlotsProvider, UnsavedNavigationGuard } from './patientSlots';
import { StepUpProvider } from './stepUp';
import LoginPage from '@/features/auth/LoginPage';
import { RouteError } from './RouteError';

function L(load: () => Promise<{ default: ComponentType }>) {
  const C = lazy(load);
  return (
    <Suspense fallback={<div role="status" aria-busy="true" aria-label="Loading"><Skeleton rows={6} /></div>}>
      <C />
    </Suspense>
  );
}

const g = (roles: RoleKey[], el: ReactNode, what?: string, permission?: string) => (
  <Guard roles={roles} what={what} permission={permission}>
    {el}
  </Guard>
);

const CLIN: RoleKey[] = ['provider', 'nurse'];
const PROV: RoleKey[] = ['provider'];
const NURSE: RoleKey[] = ['nurse', 'provider'];
const DESK: RoleKey[] = ['front_desk'];
const REV: RoleKey[] = ['revenue'];
const PRIV: RoleKey[] = ['privacy'];
const ADMIN: RoleKey[] = ['tenant_admin'];

/** Workforce routes inside the shell. Every screen of the F0+F1 inventory has a route here. */
const workspaceRoutes: RouteObject[] = [
  // Provider
  { path: 'today', element: g(PROV, L(() => import('@/features/provider/TodayPage')), 'Today') },
  { path: 'inbox', element: g(PROV, L(() => import('@/features/work/InboxPage')), 'Inbox') },
  { path: 'worklists', element: <Navigate to="/worklists/refills" replace /> },
  { path: 'worklists/:tab', element: g(PROV, L(() => import('@/features/work/WorklistsPage')), 'Worklists') },
  { path: 'reports', element: g(PROV, L(() => import('@/features/governance/ProviderReportsPage')), 'Reports') },
  // Shared
  { path: 'patients', element: g([...CLIN, ...DESK, ...REV, ...PRIV], L(() => import('@/features/patients/PatientsPage')), 'Patients') },
  { path: 'messages', element: g(['nurse', 'provider'], L(() => import('@/features/work/MessagesPage')), 'Messages', 'message.read') },
  // Nursing
  { path: 'worklist', element: g(NURSE, L(() => import('@/features/nursing/WorklistPage')), 'Worklist') },
  { path: 'tasks', element: g([...NURSE, 'front_desk'], L(() => import('@/features/nursing/TasksPage')), 'Tasks') },
  { path: 'handoff', element: g(NURSE, L(() => import('@/features/nursing/HandoffPage')), 'Handoff') },
  // Front desk
  { path: 'schedule', element: g([...DESK, ...PROV], L(() => import('@/features/frontdesk/SchedulePage')), 'Schedule') },
  { path: 'schedule/book', element: g(DESK, L(() => import('@/features/frontdesk/BookPage')), 'Booking') },
  { path: 'schedule/waitlist', element: g(DESK, L(() => import('@/features/frontdesk/WaitlistPage')), 'Waitlist') },
  { path: 'arrivals', element: g(DESK, L(() => import('@/features/frontdesk/ArrivalsPage')), 'Arrivals') },
  { path: 'registration/new', element: g([...DESK, ...REV], L(() => import('@/features/frontdesk/RegisterPage')), 'Registration', 'patient.create') },
  { path: 'registration/:patientId', element: g([...DESK, ...REV], L(() => import('@/features/frontdesk/RegistrationPage')), 'Registration', 'patient.read') },
  { path: 'identity-review', element: g([...DESK, ...REV], L(() => import('@/features/frontdesk/IdentityReviewPage')), 'Duplicate review') },
  { path: 'data-quality', element: g([...REV, ...PRIV, ...ADMIN], L(() => import('@/features/governance/DataQualityPage')), 'Data quality', 'data_quality.manage') },
  // Revenue / HIM
  { path: 'revenue', element: g(REV, L(() => import('@/features/revenue/QueuesPage')), 'Revenue work queues') },
  { path: 'revenue/superbills/:encounterId', element: g(REV, L(() => import('@/features/revenue/SuperbillPage')), 'Superbill') },
  { path: 'revenue/charges', element: g(REV, L(() => import('@/features/revenue/ChargesPage')), 'Charges') },
  { path: 'revenue/coding', element: g(REV, L(() => import('@/features/revenue/CodingQueriesPage')), 'Coding queries') },
  { path: 'revenue/accounts', element: g(REV, L(() => import('@/features/revenue/AccountsPage')), 'Accounts') },
  { path: 'revenue/accounts/:patientId', element: g(REV, L(() => import('@/features/revenue/AccountPage')), 'Account') },
  { path: 'revenue/reports', element: g(REV, L(() => import('@/features/revenue/FinancialReportPage')), 'Financial report') },
  { path: 'revenue/claims', element: g(REV, L(() => import('@/features/revenue/ClaimsOffPage')), 'Claims') },
  { path: 'revenue/records', element: g(REV, L(() => import('@/features/revenue/RecordsOffPage')), 'Records') },
  // Compliance / privacy
  { path: 'compliance', element: <Navigate to="/compliance/audit" replace /> },
  { path: 'compliance/audit', element: g(PRIV, L(() => import('@/features/compliance/AuditPage')), 'Access audit') },
  { path: 'compliance/break-glass', element: g(PRIV, L(() => import('@/features/compliance/BreakGlassReviewPage')), 'Break-glass review') },
  { path: 'compliance/disclosures', element: g(PRIV, L(() => import('@/features/compliance/DisclosuresPage')), 'Disclosures') },
  { path: 'compliance/exports', element: g(PRIV, L(() => import('@/features/compliance/ExportsPage')), 'Exports') },
  { path: 'compliance/approvals', element: g(PRIV, L(() => import('@/features/admin/AccessApprovalsPage')), 'Access approvals') },
  { path: 'downtime', element: g(['provider', 'nurse', 'front_desk', 'tenant_admin', 'privacy'], L(() => import('@/features/governance/DowntimePage')), 'Downtime packets') },
  { path: 'compliance/holds', element: g(PRIV, L(() => import('@/features/compliance/LegalHoldsPage')), 'Legal holds') },
  { path: 'compliance/report', element: g(PRIV, L(() => import('@/features/compliance/ComplianceReportPage')), 'Compliance report') },
  { path: 'compliance/consents', element: g([...PRIV, ...DESK, ...REV], L(() => import('@/features/compliance/ConsentsPage')), 'Consent and proxy', 'consent.read') },
  { path: 'compliance/consents/:patientId', element: g([...PRIV, ...DESK, ...REV], L(() => import('@/features/compliance/PatientConsentsPage')), 'Consent and proxy', 'consent.read') },
  // Tenant admin (shared admin index layout)
  {
    path: 'admin',
    element: g(ADMIN, L(() => import('@/features/admin/AdminLayout')), 'Administration'),
    children: [
      { index: true, element: L(() => import('@/features/admin/AdminHomePage')) },
      { path: 'org', element: L(() => import('@/features/admin/OrgTreePage')) },
      { path: 'users', element: L(() => import('@/features/admin/UsersPage')) },
      { path: 'roles', element: L(() => import('@/features/admin/RolesPage')) },
      { path: 'config', element: L(() => import('@/features/admin/ConfigListPage')) },
      { path: 'config/:objectId', element: L(() => import('@/features/admin/ConfigChangePage')) },
      { path: 'release', element: L(() => import('@/features/admin/ReleasePage')) },
      { path: 'terminology', element: L(() => import('@/features/admin/TerminologyPage')) },
      { path: 'support', element: L(() => import('@/features/admin/SupportPage')) },
      { path: 'access-reviews', element: L(() => import('@/features/admin/AccessReviewsPage')) },
      { path: 'ai', element: L(() => import('@/features/governance/AiGovernancePage')) },
      { path: 'cds', element: L(() => import('@/features/governance/CdsRulesPage')) },
      { path: 'ops', element: L(() => import('@/features/governance/OpsDashboardPage')) },
      { path: 'data-quality', element: L(() => import('@/features/governance/DataQualityPage')) },
      { path: 'downtime', element: L(() => import('@/features/governance/DowntimePage')) },
      { path: 'forms', element: L(() => import('@/features/chart/forms-admin/FormsAdminPage')) },
    ],
  },
  // Results interface operator: lab-feed import + unmatched results (result.import)
  { path: 'results-interface', element: g(['result_interface'], L(() => import('@/features/orders/ResultsInterfacePage')), 'Unmatched results') },
  // AI governance discipline sign-off (clinical, privacy and dedicated governance reviewers)
  { path: 'ai-signoff', element: g(['ai_governance', 'provider', 'nurse', 'privacy', 'tenant_admin'], L(() => import('@/features/governance/AiSignoffPage')), 'AI sign-off') },
  // Platform operator (no PHI)
  { path: 'platform/tenants', element: g(['platform_operator'], L(() => import('@/features/admin/TenantsPage')), 'Tenants') },
];

/** Chart sections (patient context: banner + index). */
const chartRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="summary" replace /> },
  { path: 'summary', element: g(CLIN, L(() => import('@/features/chart/SummaryPage'))) },
  { path: 'timeline', element: g(CLIN, L(() => import('@/features/chart/TimelinePage'))) },
  { path: 'problems', element: g(CLIN, L(() => import('@/features/chart/ProblemsPage'))) },
  { path: 'medications', element: g(CLIN, L(() => import('@/features/chart/MedicationsPage'))) },
  { path: 'vitals', element: g(CLIN, L(() => import('@/features/chart/VitalsPage'))) },
  { path: 'notes', element: g(CLIN, L(() => import('@/features/chart/NotesPage'))) },
  { path: 'notes/:noteId', element: g(CLIN, L(() => import('@/features/chart/NotePage'))) },
  { path: 'documents', element: g(CLIN, L(() => import('@/features/chart/DocumentsPage'))) },
  { path: 'care-team', element: g(CLIN, L(() => import('@/features/chart/CareTeamPage'))) },
  { path: 'care-gaps', element: g(CLIN, L(() => import('@/features/chart/CareGapsPage'))) },
  { path: 'results', element: g(CLIN, L(() => import('@/features/orders/ResultsPage'))) },
  { path: 'results/:resultId', element: g(CLIN, L(() => import('@/features/orders/ResultPage'))) },
  { path: 'orders', element: g(CLIN, L(() => import('@/features/orders/OrdersPage'))) },
  { path: 'orders/new', element: g(CLIN, L(() => import('@/features/orders/OrderEntryPage'))) },
  { path: 'prescriptions', element: g(PROV, L(() => import('@/features/work/PrescriptionsPage'))) },
  { path: 'referrals', element: g(PROV, L(() => import('@/features/work/PatientReferralsPage'))) },
  { path: 'allergies', element: g(CLIN, L(() => import('@/features/nursing/AllergiesPage'))) },
  { path: 'rooming', element: g(CLIN, L(() => import('@/features/nursing/RoomingPage'))) },
  { path: 'medrec', element: g(CLIN, L(() => import('@/features/nursing/MedRecPage'))) },
  { path: 'administer', element: g(CLIN, L(() => import('@/features/nursing/AdministerPage'))) },
];

const portalRoutes: RouteObject[] = [
  { index: true, element: L(() => import('@/features/portal/PortalHomePage')) },
  { path: 'appointments', element: L(() => import('@/features/portal/PortalAppointmentsPage')) },
  { path: 'results', element: L(() => import('@/features/portal/PortalResultsPage')) },
  { path: 'health', element: L(() => import('@/features/portal/PortalHealthPage')) },
  { path: 'messages', element: L(() => import('@/features/portal/PortalMessagesPage')) },
  { path: 'messages/new', element: L(() => import('@/features/portal/PortalNewMessagePage')) },
  { path: 'documents', element: L(() => import('@/features/portal/PortalDocumentsPage')) },
  { path: 'forms', element: L(() => import('@/features/portal/PortalFormsPage')) },
  { path: 'requests', element: L(() => import('@/features/portal/PortalRequestsPage')) },
  { path: 'sharing', element: L(() => import('@/features/portal/PortalSharingPage')) },
  { path: 'more', element: L(() => import('@/features/portal/PortalMorePage')) },
];

function WorkforceRoot() {
  return (
    <RequireWorkforce>
      <PatientSlotsProvider>
        <UnsavedNavigationGuard />
        <StepUpProvider>
          <Outlet />
        </StepUpProvider>
      </PatientSlotsProvider>
    </RequireWorkforce>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/portal/login', element: L(() => import('@/features/portal/PortalLoginPage')), errorElement: <RouteError /> },
  {
    path: '/portal',
    element: L(() => import('@/features/portal/PortalLayout')),
    errorElement: <RouteError />,
    children: portalRoutes,
  },
  {
    path: '/',
    element: <WorkforceRoot />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <RootRedirect /> },
      { element: <WorkspaceLayout />, children: workspaceRoutes },
      { path: 'chart/:patientId', element: <ChartLayout />, children: chartRoutes },
      { path: '*', element: <WorkspaceLayout />, children: [{ path: '*', element: L(() => import('./NotFoundPage')) }] },
    ],
  },
]);
